<template>
  <TransitionRoot appear :show="showModal" as="template">
    <Dialog as="div" @close="closeModal">
      <div class="fixed inset-0 z-30 overflow-y-auto">
        <div class="min-h-screen text-center flex">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0"
            enter-to="opacity-10"
            leave="duration-200 ease-in"
            leave-from="opacity-10"
            leave-to="opacity-0"
          >
            <DialogOverlay class="fixed inset-0 bg-black opacity-30" />
          </TransitionChild>

          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-50"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-50"
          >
            <div
              class="inline-block w-full my-5 mx-3 md:w-1/2 md:mx-auto max-w-screen-md m-auto overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded"
            >
              <div class="flex flex-row">
                <div class="flex-1"></div>
                <div class="flex-4 pr-2 pt-2 cursor-pointer" @click="closeModal">
                  <XIcon class="text-brand-dark-gray w-7 h-7" ref="close" />
                </div>
              </div>

              <template v-if="state === 'current'">
                <div class="pb-5 px-5 md:px-8">
                  <img src="@/assets/images/main-logo.svg" class="my-1 mx-auto" />
                  <h2 class="text-3xl text-brand-black text-center py-3">
                    Great, let's get started!
                  </h2>

                  <p class="py-3">
                    We will guide you through getting verified with Experian, creating your first
                    form, and sharing your applicant.
                  </p>

                  <p class="py-3 font-semibold">This process will take about 8 minutes</p>

                  <ol class="list-decimal list-inside py-1">
                    <li class="py-1">Verify with Experian to receive applicant credit reports</li>
                    <li class="py-1">Create your first form</li>
                    <li class="py-1">Share your form to start receiving applicants.</li>
                  </ol>

                  <div class="my-5 w-full md:w-2/3 mx-auto">
                    <PrimaryButton @click="startFlow('current_listing')"
                      >Verify with Experian</PrimaryButton
                    >
                  </div>
                </div>
              </template>

              <template v-else-if="state === 'setup'">
                <div class="pb-5 px-5 md:px-8">
                  <img src="@/assets/images/main-logo.svg" class="my-1 mx-auto" />
                  <h2 class="text-3xl text-brand-black text-center py-3">
                    Great, let's get set up!
                  </h2>

                  <p class="py-3">
                    We will guide you through getting verified with Experian, adding a photo to
                    increase your recognizability, creating your first form, and how to share it.
                  </p>

                  <p class="py-3 font-semibold">This process will take about 12 minutes</p>

                  <ol class="list-decimal list-inside py-1">
                    <li class="py-1">Verify with Experian to receive applicant credit reports</li>
                    <li class="py-1">Create your first form</li>
                    <li class="py-1">Learn how to share the form</li>
                  </ol>

                  <div class="my-5 w-full md:w-2/3 mx-auto">
                    <PrimaryButton @click="startFlow('future_listing')"
                      >Verify with Experian</PrimaryButton
                    >
                  </div>
                </div>
              </template>

              <template v-else-if="state === 'other'">
                <div class="pb-5 px-5 md:px-8">
                  <img src="@/assets/images/main-logo.svg" class="my-1 mx-auto" />
                  <h2 class="text-3xl text-brand-black text-center py-3">
                    What can we help you with?
                  </h2>

                  <div class="w-4/5 py-1 mx-auto">
                    <SecondaryButton
                      :classes="'border-brand-mid-gray my-2'"
                      @click="startFlow('money')"
                      >I'd like to earn money using Rent Application</SecondaryButton
                    >
                    <SecondaryButton
                      :classes="'border-brand-mid-gray my-2'"
                      @click="startFlow('team')"
                      >I want to set up Rent Application for my team</SecondaryButton
                    >
                    <SecondaryButton :classes="'border-brand-mid-gray my-2'" @click="closeModal"
                      >I'm just looking around for now</SecondaryButton
                    >
                  </div>
                </div>
              </template>

              <template v-else>
                <div class="pb-5 px-5 md:px-8">
                  <img src="@/assets/images/main-logo.svg" class="my-1 mx-auto" />
                  <h2 class="text-3xl text-brand-black text-center py-3">
                    Welcome to Rent Application
                  </h2>

                  <p class="py-3 text-center">
                    So we can best direct your experience today, what brings you to Rent
                    Application?
                  </p>

                  <div class="w-4/5 py-1 mx-auto">
                    <SecondaryButton
                      :classes="'border-brand-mid-gray my-2'"
                      @click="setState('current')"
                      >I have a current listing and I'm ready for applicants!</SecondaryButton
                    >
                    <SecondaryButton
                      :classes="'border-brand-mid-gray my-2'"
                      @click="setState('setup')"
                      >No current listings, I want to get set up so I'm prepared</SecondaryButton
                    >
                    <SecondaryButton
                      :classes="'border-brand-mid-gray my-2'"
                      @click="setState('other')"
                      >I'm here for another reason</SecondaryButton
                    >
                  </div>

                  <Line />

                  <p class="py-2">Need some help getting started?</p>

                  <p class="py-2">🇺🇸 U.S. based Rent Application staff are here to support</p>

                  <p class="py-2 text-brand-dark-gray">
                    We know that phone banks, sales calls, and automated customer service are
                    frustrating, that’s why we have real staff based in Chicago to answer your calls
                    and help guide you through getting set up and receiving your first applications.
                  </p>

                  <h5 class="py-2 text-brand-green text-lg text-center">
                    {{
                      getCurrentUser && getCurrentUser.landlord.user_type === "landlord"
                        ? "(312) 500-3121"
                        : "(872) 901-7397"
                    }}
                  </h5>

                  <p class="py-2 text-center">or</p>

                  <div class="my-3 w-full md:w-2/3 mx-auto">
                    <PrimaryButton @click="scheduleCall"
                      >Click here to schedule a call</PrimaryButton
                    >
                  </div>
                </div>
              </template>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
<script>
import { mapGetters } from "vuex"

import { TransitionRoot, TransitionChild, Dialog, DialogOverlay } from "@headlessui/vue"

import { XIcon } from "@heroicons/vue/outline"

export default {
  props: {
    showModal: {
      type: Boolean,
      default: false
    }
  },
  components: {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogOverlay,
    XIcon
  },
  computed: {
    ...mapGetters(["getCurrentUser"])
  },
  data() {
    return {
      state: "welcome"
    }
  },
  methods: {
    closeModal() {
      this.$emit("close")
    },
    scheduleCall() {
      window.open("https://calendly.com/rentapplication/intro/", "_blank")
      this.closeModal()
    },
    setState(state) {
      this.state = state
    },
    startFlow(flowType) {
      //current_listing, future_listing, money, team,
      let formatted = flowType

      if (flowType.includes("listing")) {
        formatted = "listing"
      }

      this.$router.push({
        name: `flow_${formatted}`,
        params: { current: flowType.includes("current") }
      })
    }
  }
}
</script>
<style scoped></style>
